import type {
  AdminCodeBatch,
  AdminCodeRequest,
  AdminGeneratedCodes,
  AdminGrantResult,
  AdminPlayerPage,
  AutoSellQuality,
  ConvertibleResource,
  DiamondHealResult,
  EquipmentSlot,
  GrowthProfile,
  InventoryView,
  LadderResult,
  LadderView,
  LifeProfile,
  LifeStartRequest,
  MeResponse,
  OfflineAutomationSettings,
  OfflineClaimResult,
  OfflinePurchaseResult,
  OfflineQuotaSource,
  OfflineSettingsResult,
  OfflineView,
  PlayerSettings,
  RaceReplayView,
  RaceView,
  RedeemResult,
  RegistrationPolicy,
  ResourceConversionResult,
  RoomView,
  SocialProfile,
  TaskBoard,
} from "../types";

export abstract class GameEndpoints {
  protected abstract request<T>(
    method: string,
    payload?: object,
    retried?: boolean,
    opId?: string,
  ): Promise<T>;
  async me(): Promise<MeResponse> {
    return this.request<MeResponse>("player.get");
  }

  async purchaseDiamondHeal(): Promise<DiamondHealResult> {
    return this.request<DiamondHealResult>("player.heal");
  }

  async updatePlayerSettings(autoSellQuality: AutoSellQuality): Promise<PlayerSettings> {
    return this.request<PlayerSettings>("player.settings", { autoSellQuality });
  }

  async tasks(): Promise<TaskBoard> {
    return this.request<TaskBoard>("tasks.get");
  }

  async claimTask(taskId: string): Promise<TaskBoard> {
    return this.request<TaskBoard>("tasks.claim", { taskId });
  }

  async inventory(): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.list");
  }

  async equip(itemId: string): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.equip", { itemId });
  }

  async unequip(slot: EquipmentSlot): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.unequip", { slot });
  }

  async setItemLocked(itemId: string, locked: boolean): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.lock", { itemId, locked });
  }

  async reforgeItem(itemId: string, affixIndex: number): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.reforge", { itemId, affixIndex });
  }

  async refineItem(itemId: string, affixIndex: number): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.refine", { itemId, affixIndex });
  }

  async calibrateItem(itemId: string): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.calibrate", { itemId });
  }

  async sellItems(itemIds: string[]): Promise<InventoryView> {
    return this.request<InventoryView>("inventory.sell", { itemIds });
  }

  async growth(): Promise<GrowthProfile> {
    return this.request<GrowthProfile>("growth.get");
  }

  async shop(
    action: "get" | "buy" | "refresh" | "exchange" | "exchange_crystal" = "get",
    quote = "",
    itemId = "",
    amount = 0,
  ): Promise<import("../ShopScreen").ShopView> {
    return this.request(`shop.${action}`, { quote, itemId, amount });
  }

  async life(): Promise<LifeProfile> {
    return this.request<LifeProfile>("life.get");
  }

  async startLife(request: LifeStartRequest): Promise<LifeProfile> {
    return this.request<LifeProfile>("life.start", request);
  }

  async claimLife(): Promise<LifeProfile> {
    return this.request<LifeProfile>("life.claim");
  }

  async stopLife(): Promise<LifeProfile> {
    return this.request<LifeProfile>("life.stop");
  }

  async configureSkills(
    skills: string[],
    offlineAutomation?: OfflineAutomationSettings,
    offlineSource?: OfflineQuotaSource,
  ): Promise<GrowthProfile> {
    return this.request<GrowthProfile>("skills.configure", {
      skills,
      offlineAutomation,
      offlineSource,
    });
  }

  async offline(): Promise<OfflineView> {
    return this.request<OfflineView>("offline.get");
  }

  async claimOffline(): Promise<OfflineClaimResult> {
    return this.request<OfflineClaimResult>("offline.claim");
  }

  async purchaseOffline(packageId: string): Promise<OfflinePurchaseResult> {
    return this.request<OfflinePurchaseResult>("offline.purchase", { packageId });
  }

  async updateOfflineSettings(
    preferredSource: OfflineQuotaSource,
    automation?: OfflineAutomationSettings,
  ): Promise<OfflineSettingsResult> {
    return this.request<OfflineSettingsResult>("offline.settings", { preferredSource, automation });
  }

  async unlockSkill(skillId: string): Promise<GrowthProfile> {
    return this.request<GrowthProfile>("skills.unlock", { skillId });
  }

  async respecSkills(): Promise<GrowthProfile> {
    return this.request<GrowthProfile>("skills.respec");
  }

  async upgradeWorkshop(buildingId: string): Promise<GrowthProfile> {
    return this.request<GrowthProfile>("workshop.upgrade", { buildingId });
  }

  async convertWorkshopResources(
    sourceCurrency: ConvertibleResource,
    targetCurrency: ConvertibleResource,
    sourceAmount: number,
  ): Promise<ResourceConversionResult> {
    return this.request<ResourceConversionResult>("workshop.convert", {
      sourceCurrency,
      targetCurrency,
      sourceAmount,
    });
  }

  async redeemCode(code: string): Promise<RedeemResult> {
    return this.request<RedeemResult>("redeem.code", { code });
  }

  async createRoom(
    mode: "raid" | "duo",
    visibility: "public" | "guild" | "friends" | "invite" = "invite",
  ): Promise<RoomView> {
    return this.request<RoomView>("room.create", { mode, visibility });
  }

  async listRooms(
    mode: "raid" | "duo" | "" = "",
    minDepth = 0,
    maxDepth = 0,
  ): Promise<import("../types").RoomListing[]> {
    return this.request<import("../types").RoomListing[]>("room.list", {
      mode,
      minDepth,
      maxDepth,
    });
  }

  async updateRoom(
    mode: "raid" | "duo",
    visibility: "public" | "guild" | "friends" | "invite",
  ): Promise<RoomView> {
    return this.request<RoomView>("room.update", { mode, visibility });
  }

  async kickRoomMember(userId: string): Promise<RoomView> {
    return this.request<RoomView>("room.kick", { userId });
  }

  async transferRoomOwner(userId: string): Promise<RoomView> {
    return this.request<RoomView>("room.owner.transfer", { userId });
  }

  async acknowledgeRoomResult(): Promise<RoomView> {
    return this.request<RoomView>("room.result.ack");
  }

  async matchRaid(): Promise<RoomView> {
    return this.request<RoomView>("raid.match");
  }

  async joinRoom(inviteCode: string): Promise<RoomView> {
    return this.request<RoomView>("room.join", { inviteCode });
  }

  async inviteRoom(username: string): Promise<RoomView> {
    return this.request<RoomView>("room.invite", { username });
  }

  async roomReady(ready: boolean): Promise<RoomView> {
    return this.request<RoomView>("room.ready", { ready });
  }

  async startRoom(solo = false): Promise<RoomView> {
    return this.request<RoomView>("room.start", { solo });
  }

  async currentRoom(): Promise<RoomView> {
    return this.request<RoomView>("room.current");
  }

  async leaveRoom(): Promise<RoomView> {
    return this.request<RoomView>("room.leave");
  }

  async roomSlide(
    axis: "row" | "col",
    index: number,
    distance: number,
    boardHash: string,
    automated = false,
  ): Promise<RoomView> {
    return this.request<RoomView>("room.slide", { axis, index, distance, boardHash, automated });
  }

  async roomSkill(skill: string, targetUserId = ""): Promise<RoomView> {
    return this.request<RoomView>("room.skill", { skill, targetUserId });
  }

  async roomRetreat(confirm = false): Promise<RoomView> {
    return this.request<RoomView>("room.retreat", { confirm });
  }

  async startRace(kind: "daily" | "weekly"): Promise<RaceView> {
    return this.request<RaceView>("race.start", { kind });
  }

  async currentRace(): Promise<RaceView> {
    return this.request<RaceView>("race.current");
  }

  async raceSlide(
    axis: "row" | "col",
    index: number,
    distance: number,
    boardHash: string,
  ): Promise<RaceView> {
    return this.request<RaceView>("race.slide", { axis, index, distance, boardHash });
  }

  async raceDraft(choice: string): Promise<RaceView> {
    return this.request<RaceView>("race.draft", { choice });
  }

  async raceSkill(skill: string): Promise<RaceView> {
    return this.request<RaceView>("race.skill", { skill });
  }

  async raceReplay(entryId: string): Promise<RaceReplayView> {
    return this.request<RaceReplayView>("race.replay", { entryId });
  }

  async social(): Promise<SocialProfile> {
    return this.request<SocialProfile>("social.get");
  }

  async friendRequest(username: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("friend.request", { username });
  }

  async friendRespond(userId: string, accept: boolean): Promise<SocialProfile> {
    return this.request<SocialProfile>("friend.respond", { userId, accept });
  }

  async friendRemove(userId: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("friend.remove", { userId });
  }

  async createCrew(name: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("crew.create", { name });
  }

  async joinCrew(name: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("crew.join", { name });
  }

  async leaveCrew(): Promise<SocialProfile> {
    return this.request<SocialProfile>("crew.leave");
  }

  async claimCrewSupply(): Promise<SocialProfile> {
    return this.request<SocialProfile>("crew.supply");
  }

  async updateGuild(
    announcement: string,
    joinPolicy: "open" | "approval" | "invite_only",
  ): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.update", { announcement, joinPolicy });
  }

  async respondGuildApplication(userId: string, accept: boolean): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.application.respond", { userId, accept });
  }

  async inviteGuild(username: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.invite", { username });
  }

  async respondGuildInvite(guildId: string, accept: boolean): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.invite.respond", { guildId, accept });
  }

  async setGuildRole(userId: string, role: "officer" | "member"): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.role", { userId, role });
  }

  async removeGuildMember(userId: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.remove", { userId });
  }

  async transferGuild(userId: string): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.transfer", { userId });
  }

  async archiveGuild(): Promise<SocialProfile> {
    return this.request<SocialProfile>("guild.archive");
  }

  async adminPlayers(query = "", limit = 20, offset = 0): Promise<AdminPlayerPage> {
    return this.request<AdminPlayerPage>("admin.players", { query, limit, offset });
  }

  async adminRegistrationPolicy(): Promise<RegistrationPolicy> {
    return this.request<RegistrationPolicy>("admin.registration.get");
  }

  async adminCodeBatches(offset = 0): Promise<AdminCodeBatch[]> {
    return this.request("admin.codes.list", { limit: 20, offset });
  }

  async ladder(): Promise<LadderView> {
    return this.request("ladder.get");
  }

  async socketGem(
    itemId: string,
    gemId: string,
    index: number,
    remove: boolean,
  ): Promise<{ result: { destroyed: boolean }; inventory: InventoryView }> {
    return this.request(remove ? "inventory.unsocket" : "inventory.socket", {
      itemId,
      gemId,
      index,
    });
  }

  async registerLadder(skills: string[]): Promise<LadderResult> {
    return this.request("ladder.register", { skills });
  }

  async purchaseLadderAttempts(): Promise<LadderResult> {
    return this.request("ladder.purchase");
  }

  async challengeLadder(): Promise<LadderResult> {
    return this.request("ladder.challenge");
  }

  async adminCreateCodes(payload: AdminCodeRequest, opId: string): Promise<AdminGeneratedCodes> {
    return this.request("admin.codes.create", payload, false, opId);
  }

  async adminRevealCodes(batchId: string): Promise<AdminGeneratedCodes> {
    return this.request("admin.codes.reveal", { batchId });
  }

  async updateAdminRegistrationLimit(registrationLimit: number): Promise<RegistrationPolicy> {
    return this.request<RegistrationPolicy>("admin.registration.update", { registrationLimit });
  }

  async adminGrant(
    targetUserId: string,
    resources: { wood: number; ore: number; gold: number; crystal: number },
  ): Promise<AdminGrantResult> {
    return this.request<AdminGrantResult>("admin.grant", { targetUserId, resources });
  }
}
